import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { SelectEntidade } from "@/components/SelectEntidade";
import { fetchAllUsers } from "@/api/allUsers";
import { listModuleRoleGrants } from "@/api/moduleRole.service";
import { MODULE_ID, type Escala } from "@/api/escala.service";
import type { User } from "@/api/user.service";

interface EscalaResponsavelModalProps {
  // Escala a transferir — null fecha o modal.
  escala: Escala | null;
  clientId: string;
  onClose: () => void;
  onSubmit: (novoResponsavelId: string) => Promise<void>;
}

// Só quem tem papel na Escala pode assumir uma escala — o backend também
// recusa o resto, mas oferecer só esses evita o erro de propósito.
export function EscalaResponsavelModal({ escala, clientId, onClose, onSubmit }: EscalaResponsavelModalProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [comPapel, setComPapel] = useState<Set<string>>(new Set());
  const [carregando, setCarregando] = useState(false);
  const [erroCarga, setErroCarga] = useState(false);
  const [novoId, setNovoId] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!escala) return;
    setNovoId(null);
    setErroCarga(false);
    setCarregando(true);
    Promise.all([fetchAllUsers(), listModuleRoleGrants(clientId, MODULE_ID)])
      .then(([todos, grants]) => {
        setUsers(todos);
        setComPapel(new Set(grants.map((g) => g.user_id)));
      })
      .catch(() => setErroCarga(true))
      .finally(() => setCarregando(false));
  }, [escala, clientId]);

  const candidatos = useMemo(
    () => users.filter((u) => comPapel.has(u.id) && u.id !== escala?.created_by),
    [users, comPapel, escala],
  );
  const atual = users.find((u) => u.id === escala?.created_by);

  const handleConfirmar = async () => {
    if (!novoId) return;
    setSalvando(true);
    try {
      await onSubmit(novoId);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={!!escala} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Transferir responsável</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <Typography variant="body2" color="text.secondary">
          {escala?.nome} — responsável atual: {atual?.name ?? "não encontrado"}. O Encarregado só enxerga as
          escalas em que é o responsável.
        </Typography>
        {erroCarga && <Alert severity="error">Erro ao carregar os usuários da Escala</Alert>}
        <SelectEntidade
          label="Novo responsável"
          options={candidatos}
          value={novoId}
          onChange={setNovoId}
          getLabel={(u) => u.name}
          disabled={carregando}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleConfirmar} variant="contained" disabled={!novoId || salvando}>
          Transferir
        </Button>
      </DialogActions>
    </Dialog>
  );
}
